import ApexCharts from "apexcharts";

// Replicates the fork's rgba(r, g, b, value/100) coloring.
// Values above 100 get full opacity, matching fork behaviour.
const buildColorRanges = () => {
  const ranges = [];
  for (let v = 0; v <= 100; v++) {
    ranges.push({ from: v, to: v, color: `rgba(0,174,239,${(v / 100).toFixed(2)})` });
  }
  ranges.push({ from: 101, to: 10000, color: "rgba(0,174,239,1.00)" });
  return ranges;
};

const COLOR_RANGES = buildColorRanges();

const useChartConfig = (props, chartEvents) => {
  const chartId = props.chartId || "heatmap-chart";

  const exportPng = async () => {
    const chart = ApexCharts.getChartByID(chartId);
    if (!chart) return;
    const { imgURI } = await chart.dataURI();
    const link = document.createElement("a");
    link.href = imgURI;
    link.download = "HeatMap.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const chartConfig = {
    options: {
      tooltip: {
        enabled: false
      },
      legend: {
        show: false
      },
      stroke: {
        width: props.stroke,
        colors: ["#353942"]
      },
      grid: {
        show: true,
        borderColor: "#353942",
        padding: { right: -10, bottom: 4 },
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            // v5 uses `colors` (array or string) instead of v3's `color`
            colors: "white",
            fontSize: "15px",
            fontWeight: 800
          }
        }
      },
      xaxis: {
        axisTicks: {
          show: true,
          height: -6,
          tickPlacement: "between",
          color: "#78909c"
        },
        axisBorder: { color: "#78909c" },
        type: "category",
        categories: props.categories
          ? props.categories
          : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        position: "top",
        labels: {
          show: true,
          style: {
            fontSize: "15px",
            fontWeight: 800,
            colors: "white"
          }
        }
      },
      chart: {
        // Stable id allows ApexCharts.getChartByID() lookup for programmatic export.
        // Must be unique per mounted chart instance on the page.
        id: props.chartId || "heatmap-chart",
        toolbar: {
          show: false,
          export: {
            png: {
              filename: "HeatMap"
            }
          }
        },
        background: "#262931",
        animations: {
          dynamicAnimation: { enabled: false }
        },
        events: chartEvents
      },
      dataLabels: {
        enabled: true,
        enabledOnSeries: undefined,
        textAnchor: "middle",
        offsetX: 0,
        offsetY: 0,
        style: {
          fontSize: "16px",
          fontWeight: 800,
          fontFamily: "Roboto, Arial, sans-serif",
          colors: ["#dedede"]
        },
        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 1,
          opacity: 0.45
        },
        // Replaces the fork's formatDisplayValue() logic applied in the engine.
        // Reads per-datapoint fields (displayValue, valueSymbol, modalData) that
        // apexcharts preserves on w.config.series — no patch needed.
        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
          const dataPoint = w.config.series[seriesIndex]?.data?.[dataPointIndex];
          if (!dataPoint) return value;

          const { displayValue, valueSymbol, modalData } = dataPoint;

          // Empty modalData object means no data for this cell
          if (modalData && Object.keys(modalData).length === 0) {
            return "-";
          }

          // Prefix non-percentage symbol (e.g. currency) before the value
          if (valueSymbol && valueSymbol !== "" && valueSymbol !== "%") {
            return valueSymbol + displayValue;
          }

          return displayValue ?? value;
        }
      },
      states: {
        hover: {
          filter: { type: "lighten", value: 0.15 }
        },
        active: {
          filter: { type: "none" }
        }
      },
      fill: {
        opacity: 1,
        type: "solid"
      },
      plotOptions: {
        heatmap: {
          radius: props.heatMapRadius,
          enableShades: false,
          colorScale: {
            ranges: COLOR_RANGES
          }
        }
      }
    },
    series: props.series
  };

  return { chartConfig, exportPng };
};

export default useChartConfig;
